// @flow

import * as React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import './styles.scss'
import Masthead from '@ta-interaktiv/react-masthead'
import { ImageCover } from '../cover'
import { TimestampFormatter } from '@ta-interaktiv/react-publication-info'
import sheetrock from 'sheetrock'
import { zip } from 'd3-array'
import { HotelCategory } from 'components/hotelCategory'
import { CategoryMenu } from 'components/categoryMenu'
import { Sidebar } from 'components/slidingpane'
import { RatingExplanationButton } from 'components/ratingExplanationButton'
import { withRail } from 'components/rail'
import { ArticleList } from 'components/articleList'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import ShareButtons from '@ta-interaktiv/react-share-buttons'
import { FeedbackMessage } from '@ta-interaktiv/react-feedback-message'
import Waypoint from 'react-waypoint'
import { PureCopyrightNotice } from '@ta-interaktiv/react-copyright-notice'
import config from 'config'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'

const posterImage = require('images/The Chedi' +
  ' (41).jpg?sizes[]=640w&sizes[]=1280w&sizes[]=1800w&sizes[]=2360w&placeholder')
const RailedButton = withRail(RatingExplanationButton)

/**
 * The article ID of the corresponding CD story
 *
 * @todo Add actual article ID
 * @type {number}
 */
const ARTICLE_ID = 25614010

type SheetrockResponse = {
  attributes: {
    last: number,
    rowNumberOffset: 0,
    labels: Array<string>
  },
  html: string,
  options: any,
  raw: {
    version: string,
    reqId: string,
    status: string,
    sig: string,
    table: {
      cols: Array<{
        id: string,
        label: string,
        type: string,
        pattern?: string
      }>,
      rows: Array<{ v: string | number | null, f?: string } | null>
    }
  },
  rows: Array<{ cellsArray: Array<string>, labels: Array<string>, num: number }>
}

type Props = {}

type State = {
  request: 'loading' | 'error' | 'ready',
  hotelsByCategory: Map<string, Array<Map<string, any>>>,
  publishedTimestamp: number
}

function checkResponseStatus(response) {
  if (response.ok) {
    return response.json()
  }

  throw new Error(`${response.status}: ${response.message}`)
}

export default class AppComponent extends React.Component<Props, State> {
  state = {
    request: 'loading',
    hotelsByCategory: new Map(),
    publishedTimestamp: 0
  }

  componentDidMount() {
    ThemeSwitch({})

    if (navigator.userAgent !== "ReactSnap") {
      sheetrock({
        url:
          'https://docs.google.com/spreadsheets/d/1imIiwtKxTWoOgDlErzkqmguEboF6ZWbF_DLCFmw8uBQ/edit#gid=0',
        query: 'select A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R where A != ""',
        callback: this.prepareData.bind(this)
      })

      const domain = parseHostname()

      window
        .fetch(`https://m.${domain.publicationDomain}/api/articles/${ARTICLE_ID}`)
        .then(checkResponseStatus)
        .then(data => {
          this.setState({
            publishedTimestamp: data.article.timestamp_updated_at
          })
        })
        .catch(error => {
          console.warn(
            `Article data for ID ${ARTICLE_ID} could not be loaded. Reason: ${error}`
          )
        })

    }
  }

  /**
   * Prepare the hotel data and set the state once its ready
   * @param error
   * @param options
   * @param response
   */
  prepareData(error, options, response: SheetrockResponse) {
    // Error handling
    if (error) {
      console.group('Main Component 🗂 Sheetrock Loader')
      console.log(error)
      console.groupEnd()
      this.setState({
        request: 'error'
      })
      return
    }

    const mapRows = response.rows.map((row, index) => {
      return new Map(zip(row.labels, row.cellsArray))
    })

    // Check that we actually have data
    console.assert(
      mapRows[1].get('Kategorie') ===
        'Die 25 besten' + ' Winterhotels mit 5 Sternen'
    )

    // Create a new map
    let hotelsByCategory: Map<string, Array<Map<string, any>>> = new Map()

    // Get the categories in the configuration and pull all the hotels for
    // each category out of the list
    Object.keys(config.categories).forEach(category => {
      hotelsByCategory.set(
        category,
        mapRows.filter(hotel => hotel.get('Kategorie') === category)
      )
    })

    // Push to state
    this.setState({
      hotelsByCategory,
      request: 'ready'
    })
  }

  render() {
    return (
      <div className='index'>
        <Masthead
          articleId={ARTICLE_ID}
          inverted
          hashtags={['switzerland', 'hotels', 'holidays']}
          mediaName={'sonntagszeitung'}
        />
        {/*
        Cover Image
        /src/components/cover/image.js
        */}
        <ImageCover
          inverted
          poster={posterImage}
          title='Die besten Winterhotels der Schweiz'
          subtitle='Die besten Häuser für Ihre Winterferien: Aufsteiger, Absteiger, und zehn Neue.'
          coverHeight='85vh'
          position='top'
          padding='100px'
          showTextShadow={false}
          showTopGradient={false}
        />

        <div className='ui very fitted vertical segment'>
          <div className='ui container'>
            <p className='ta secondary caption'>
              The Chedi Andermatt steht vor allem für eines: für Weltklasse
              total – auf allen Ebenen und in jeder Hinsicht.
            </p>
          </div>
        </div>

        <main className='ui vertical segment'>
          <div className='ui text container'>
            <p>
              <cite>Von Karl Wild</cite>
            </p>

            <p>
              In der Rangliste der besten Hotels mit fünf Sternen hat The Chedi
              Andermatt das Kulm St. Moritz von der Spitze verdrängt. Das
              Herzstück von Samih Sawiris' Tourismus-Milliardenprojekt im
              Urserental steht fünf Jahre nach der Eröffnung vor allem für
              eines: für Weltklasse total – auf allen Ebenen und in jeder
              Hinsicht. Hauptsächlich dank Schweizer Gästen ist das spektakuläre
              Alpen-Hideaway alljährlich im zweistelligen Bereich gewachsen.
              Jetzt schreibt es erstmals schwarze Zahlen. Ohne jegliches
              Schräubeln an den Zimmerpreisen. Und, wohlgemerkt, bei
              ganzjähriger Öffnungszeit (die meisten vergleichbaren Häuser sind
              sechs bis sieben Monate offen). Das Prädikat Weltklasse trifft
              freilich auch auf mehrere andere Schweizer Luxusherbergen zu.
              Allen voran auf das St. Moritzer Kulm, ein Ferienresort der
              unbegrenzten Möglichkeiten mit 40 prächtigen neuen Zimmern. Oder
              auf das drittplatzierte Tschuggen in Arosa mit der neuen Version
              des Tschuggen-Express, der die Gäste vom Hotel direkt mitten ins
              Skigebiet bringt.
            </p>

            <Waypoint
              scrollableAncestor={window}
              onEnter={event => {
                if (event.previousPosition === 'below') {
                  window.dataLayer = window.dataLayer || []
                  console.log('waypoint')
                  window.dataLayer.push({
                    event: 'startReading'
                  })
                }
              }}
            />

            {/*
            Button «So wurde bewertet»
            /src/components/ratingExplanationButton/component.js
            */}
            <RailedButton side='right' className='center aligned' />

            <p>
              Einen Führungswechsel gab es auch in der Liste der Tophotels mit
              vier Sternen. Das Guarda Val oberhalb von Lenzerheide löste das
              Pirmin Zurbriggen in Saas-Almagell ab. Für das Maiensässhotel im
              Weiler Sporz spricht vor allem seine Aura der Einzigartigkeit. Es
              gibt in den Alpen nichts Vergleichbares zum raffinierten Luxus
              hinter den verwitterten Balken des Guarda Val. Die Nachahmungen,
              die im Ausland hingestellt wurden, sind Kopien geblieben. Nichts
              zu rütteln gab es bei den Dreisternhotels an der Leaderposition
              des Hotels Spitzhorn. Auch fünf Jahre nach der Eröffnung bleibt
              das Chalet in Gstaad-Saanen unangefochten die Nummer eins vor dem
              St. Moritzer Kulthotel Waldhaus am See. Interessantester Neuling
              im Rating ist das Ultima Gstaad. Das fünfte Luxushotel im Berner
              Oberländer Nobelkurort mit exklusiven Suiten und Residenzen in
              drei riesigen Antikholz-Chalets legte einen fulminanten Start hin
              und holte sich gleich einen Platz in den Top Ten. Bemerkenswert
              auch das Comeback des Kempinski St. Moritz. Hier zeigt sich
              beispielhaft, was eine engagierte neue Führung bewirken kann.
            </p>

            {/*
            Box «Weitere Stories zum Hotelrating
            /src/components/articleList/index.js
            */}
            <ArticleList />

            <p>
              Nur bedingt aussagekräftig sind, mittlerweile erst recht, die
              aufgeführten Zimmerpreise. Flexible Preise setzen sich immer mehr
              durch und werden vom Gast auch zunehmend akzeptiert. Wo dieser
              Preis liegt, erfragt man am besten im Hotel. Der direkte Kontakt
              bringt nur Vorteile. Und macht Buchungsportale überflüssig.
            </p>
          </div>
        </main>

        {/* Sidebar – sollte so weit noch korrekt sein */}
        <Sidebar title='So wurde bewertet' className='infographic'>
          <p>
            <strong>Karl Wild</strong> ist Verfasser des Ratings mit den besten
            Winterhotels. Er arbeitet als Publizist, Hotel- und Restauranttester
            in Langnau am Albis ZH.{' '}
            <span className='mobile hidden'>
              Er und sein Team, mehr als ein Dutzend Spezialisten aus Hotellerie
              und Tourismus sowie Vielreisende, haben alle Hotels besucht. Um
              die Chancengleichheit zu wahren, werden nur Hotels mit mindestens
              12 Zimmern und eigenem Restaurant berücksichtigt.
            </span>
          </p>
          <h3 id='die-kriterien' className='ui tiny dividing header'>
            Die Kriterien
          </h3>
          <ul className='ui list'>
            <li className={'item'}>
              Wertung der wichtigen Hotel- und Restaurantführer
            </li>
            <li className={'item'}>
              Qualitätskontrollen führender Hotelvereinigungen
            </li>
            <li className={'item'}>Investitionstätigkeit</li>
            <li className={'item'}>Gastfreundschaft</li>
            <li className={'item'}>
              Charisma und Innovationsfreude des Hoteliers
            </li>
            <li className={'item'}>Charakter und Originalität des Hauses</li>
            <li className={'item'}>Lage und Umgebung</li>
            <li className={'item'}>Wintersport- und Freizeitangebot</li>
            <li className={'item'}>Preis-Leistungs-Verhältnis</li>
            <li className={'item'}>Gästebewertungen</li>
            <li className={'item'}>Subjektiver Gesamteindruck</li>
          </ul>
        </Sidebar>
        <div>
          {/*
        Menu «Die besten Hotels in den Kategorien …»
        /src/components/categoryMenu/component.js
        */}
          <div className='ui container'>
            <div className='ui bottom pointing large blue basic label'>
              Wählen Sie eine Kategorie
            </div>
          </div>
          <CategoryMenu />

          {/*
        Die Liste der Hotels. Diese Funktion nimmt die in
         /src/config/base.js hinterlegten Kategorienamen und generiert ein
          eigenes .ui.segment für jede der Kategorien.

          Bewertungs-Kategorie
           /src/components/hotelCategory/component.js
        */}
          {Object.keys(config.categories).map((category: string) => (
            <HotelCategory
              key={config.textToClassName(category)}
              category={category}
              id={`cat-${config.textToClassName(category)}`}
              hotelList={this.state.hotelsByCategory.get(category) || []}
              request={this.state.request}
            />
          ))}
        </div>

        <Waypoint
          scrollableAncestor={window}
          onEnter={event => {
            if (event.previousPosition === 'below') {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'contentBottom'
              })
            }
          }}
        />

        <footer className='ui vertical attached segment'>
          <div className='ui container'>
            <div className='ui stackable grid'>
              <div className='two column divided row'>
                <div className='column'>
                  <table className='ui basic definition infographic small table'>
                    <tbody>
                      <tr className='top aligned'>
                        <td>Hotelranking und Texte</td>
                        <td>Karl Wild</td>
                      </tr>
                      <tr className='top aligned'>
                        <td>Digitale Umsetzung</td>
                        <td>Kaspar Manz und Patrick Vögeli, Interaktiv-Team</td>
                      </tr>
                      {this.state.publishedTimestamp > 0 && (
                        <tr>
                          <td>Veröffentlicht</td>
                          <td>
                            <TimestampFormatter
                              timestamp={this.state.publishedTimestamp}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className='column'>
                  <FeedbackMessage mailTo={'interaktiv@tamedia.ch'} />
                  <ShareButtons
                    articleId={ARTICLE_ID}
                    className='basic'
                    hashtags={['holidays', 'switzerland', 'hotels']}
                  />
                </div>
              </div>
              <div className='one column row'>
                <div className='center aligned column'>
                  <PureCopyrightNotice
                    name='SonntagsZeitung'
                    secondLevelDomainName='sonntagszeitung'
                  />
                </div>
              </div>
            </div>
          </div>
        </footer>

        <Waypoint
          scrollableAncestor={window}
          onEnter={event => {
            if (event.previousPosition === 'below') {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'pageBottom'
              })
            }
          }}
        />
      </div>
    )
  }
}
